import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "../components/Navbar";
import { api } from "../lib/api";
import {
  speak, cancelSpeech, createRecognizer, similarity, detectVoiceCommand, waitForVoices, cues,
} from "../lib/speech";
import {
  Play, Pause, Microphone, ArrowLeft, ArrowRight, ArrowClockwise, SpeakerHigh, CheckCircle, XCircle,
  FastForward, Headphones, GearSix, Target, Lightbulb, BookOpen, Sparkle, X, Crown, GraduationCap,
  Flame, Snowflake, Trophy, Eye,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import AdSlot from "../components/AdSlot";

const PRACTICE_FIRST_KEY = "ekkol_practice_first_shown";
const LEARN_FIRST_KEY = "ekkol_learn_first_shown";
// Legacy key used by iteration 10 — carry it forward so existing users aren't re-prompted
const LEGACY_FIRST_KEY = "ekkol_first_session_shown";
const UPSELL_COUNTER_KEY = "ekkol_session_upsell_counter";
const UPSELL_DISMISS_KEY = "ekkol_upsell_dismissed_until";

export default function StudySession() {
  const { deckId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();

  const [deck, setDeck] = useState(null);
  const [cards, setCards] = useState([]);
  const [idx, setIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [finished, setFinished] = useState(false);
  const [failedCards, setFailedCards] = useState([]); // card objects that failed during this session
  const [sessionResults, setSessionResults] = useState([]); // [{card_id, content, prompt, passed, score, spoken}]
  const [summary, setSummary] = useState("");
  const [summaryLoading, setSummaryLoading] = useState(false);

  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [transcriptFinal, setTranscriptFinal] = useState("");

  // First-time study "how this works" overlay (per-user, localStorage-gated, per-mode)
  // Practice-mode first-time overlay (carries over legacy key from iteration 10)
  const [showPracticePrep, setShowPracticePrep] = useState(() => {
    try {
      if (localStorage.getItem(PRACTICE_FIRST_KEY) === "1") return false;
      // Legacy migration — if user already dismissed the old single-overlay, don't nag
      if (localStorage.getItem(LEGACY_FIRST_KEY) === "1") {
        try { localStorage.setItem(PRACTICE_FIRST_KEY, "1"); } catch {}
        return false;
      }
      return true;
    } catch { return false; }
  });
  const [showLearnPrep, setShowLearnPrep] = useState(false);

  const dismissPracticePrep = (remember = true) => {
    if (remember) { try { localStorage.setItem(PRACTICE_FIRST_KEY, "1"); } catch {} }
    setShowPracticePrep(false);
  };
  const dismissLearnPrep = (remember = true) => {
    if (remember) { try { localStorage.setItem(LEARN_FIRST_KEY, "1"); } catch {} }
    setShowLearnPrep(false);
  };
  const openPracticePrep = () => setShowPracticePrep(true);
  const openLearnPrep = () => setShowLearnPrep(true);

  // Free-tier gentle upsell after summary (once per 5 sessions, snoozable)
  const [showUpsell, setShowUpsell] = useState(false);
  const [streakDelta, setStreakDelta] = useState(null);
  const [transcriptInterim, setTranscriptInterim] = useState("");
  const [matchScore, setMatchScore] = useState(null);
  const [scoredPass, setScoredPass] = useState(null);
  const [scoreReason, setScoreReason] = useState("");
  const [retries, setRetries] = useState(0);
  const [paused, setPaused] = useState(false);

  // Settings
  const [handsFree, setHandsFree] = useState(false);
  const [matchMode, setMatchMode] = useState("exact");
  const [threshold, setThreshold] = useState(75);
  const [autoAdvance, setAutoAdvance] = useState(true);
  const [rate, setRate] = useState(1);
  const [voiceURI, setVoiceURI] = useState("");
  const [voices, setVoices] = useState([]);
  const [showSettings, setShowSettings] = useState(false);
  // "beep" (default) | "auto" | "bargein"
  const [listenMode, setListenMode] = useState("beep");
  // "off" | "after3" | "endofrun"
  const [revealMode, setRevealMode] = useState("off");
  const [missCountByCard, setMissCountByCard] = useState({});
  // Mirror of missCountByCard as a ref — gradeAttempt is invoked from inside
  // recognizer callbacks whose closure cannot be re-subscribed per state
  // change. Without this ref, newMisses was always stuck at 1 and the
  // attempts-before-reveal threshold never fired. RETRY 7+ bug sound familiar?
  const missCountRef = useRef({});
  // auto-hint setting — 0 = off, else hint automatically after N misses.
  // Default is 2 — by the second wrong attempt most users appreciate a nudge
  // without the answer yet. Profile lets them raise it or turn it off.
  const [autoHintAfterN, setAutoHintAfterN] = useState(2);
  // Current hint state for the current card
  const [currentHint, setCurrentHint] = useState("");
  const [hintLevel, setHintLevel] = useState(0); // 0 = none requested yet
  const hintLevelRef = useRef(0);
  useEffect(() => { hintLevelRef.current = hintLevel; }, [hintLevel]);
  // Per-session set of card_ids where auto-hint has already fired. Prevents
  // the same hint request from stacking up across misses / retries. Cleared
  // on deck change and session restart.
  const autoHintFiredRef = useRef(new Set());
  const [hintLoading, setHintLoading] = useState(false);
  // Manually revealed (user tapped "Show answer"). Resets when the card changes.
  const [manualReveal, setManualReveal] = useState(false);
  // Max wrong attempts before Ekkol auto-reveals the answer. 3 by default.
  // 0 = never auto-reveal (user controls via Show answer button).
  const [attemptsBeforeReveal, setAttemptsBeforeReveal] = useState(3);
  // Per-card set of card_ids the user has seen the answer for (via manual
  // Show answer OR auto-reveal). These are pushed into the Retry pile so
  // "cards you peeked at" get another pass at the end of the session.
  const [revealedCardIds, setRevealedCardIds] = useState(() => new Set());

  // --- Learn mode (non-scored listen+repeat) ---
  // "practice" = current behaviour (mic + grading); "learn" = guided audio playback
  const [sessionMode, setSessionMode] = useState(() => searchParams.get("mode") === "learn" ? "learn" : "practice");
  // Smart Review (SRS) — loads only due + new cards from /api/reviews/due
  // instead of the full deck. Grading/retry/hint flow is IDENTICAL to
  // regular Practice mode (spoken scoring, SRS submits on every attempt).
  const smartMode = searchParams.get("mode") === "smart";
  // Show the Learn-mode intro the first time this user lands in Learn mode
  useEffect(() => {
    if (sessionMode !== "learn") return;
    try {
      if (localStorage.getItem(LEARN_FIRST_KEY) !== "1") {
        setShowLearnPrep(true);
      }
    } catch {}
  }, [sessionMode]);
  // Pause length: "short" | "medium" | "long" | "auto" (auto scales with text length)
  const [learnPause, setLearnPause] = useState("medium");
  // For Script decks: read each chunk twice (once normal, once slightly slower)
  const [learnScriptRepeat, setLearnScriptRepeat] = useState(false);
  // Master toggle for voice commands (next/back/repeat/pause/stop/hint). User-controlled via Profile.
  const [voiceCommandsEnabled, setVoiceCommandsEnabled] = useState(true);
  const voiceCommandsEnabledRef = useRef(true);
  useEffect(() => { voiceCommandsEnabledRef.current = voiceCommandsEnabled; }, [voiceCommandsEnabled]);
  // In-session help for mode explainer
  const [showModeHelp, setShowModeHelp] = useState(false);
  // Internal cancellation token for the Learn-mode async sequence
  const learnCancelRef = useRef(0);

  const recRef = useRef(null);
  const modeRef = useRef({ handsFree: false, autoAdvance: true, listenMode: "beep" });
  modeRef.current = { handsFree, autoAdvance, listenMode };
  const idxRef = useRef(0);
  idxRef.current = idx;
  const cardsRef = useRef([]);
  cardsRef.current = cards;
  const retriesRef = useRef(0);
  retriesRef.current = retries;
  const scoredRef = useRef(null);
  scoredRef.current = scoredPass;
  const pausedRef = useRef(false);
  pausedRef.current = paused;

  const card = cards[idx];
  const isFlashcard = deck?.mode === "qa" && deck?.qa_style === "flashcard";
  // In flashcard mode, TTS reads the prompt and user must say the answer (card.content).
  const textToRead = (c) => (isFlashcard && c?.prompt) ? c.prompt : (c?.content || "");
  const textToMatch = (c) => c?.content || ""; // always the answer / target memorized text

  // Load deck + voices + profile prefs
  useEffect(() => {
    // Start clean whenever deckId changes (fresh session, fresh counters).
    missCountRef.current = {};
    autoHintFiredRef.current = new Set();
    (async () => {
      try {
        const { data } = await api.get(`/decks/${deckId}`);
        if (!data.cards?.length) {
          toast.error("This deck is empty");
          navigate(`/decks/${deckId}`);
          return;
        }
        setDeck(data.deck);
        setMatchMode(data.deck.match_mode || "exact");
        setThreshold(data.deck.match_threshold ?? 75);
        setAutoAdvance(data.deck.auto_advance ?? true);
        if (smartMode) {
          // Smart Review — fetch only due + new cards. If nothing is due,
          // gracefully fall back to the full deck with a toast so the user
          // isn't dumped back to DeckView confused about why they clicked
          // "Smart Review" and got nothing.
          try {
            const { data: due } = await api.get(`/reviews/due`, { params: { deck_id: deckId, limit: 50 } });
            if (due.cards && due.cards.length > 0) {
              setCards(due.cards);
              const label = due.due_count > 0 && due.new_count > 0
                ? `${due.due_count} due · ${due.new_count} new`
                : due.due_count > 0
                  ? `${due.due_count} due for review`
                  : `${due.new_count} new cards`;
              toast.success(`Smart Review: ${label}`, { duration: 3500 });
            } else {
              setCards(data.cards);
              toast("Nothing due yet — starting a full deck practice", { duration: 4000 });
            }
          } catch {
            setCards(data.cards);
          }
        } else {
          setCards(data.cards);
        }
      } catch {
        toast.error("Could not load session");
        navigate("/dashboard");
      } finally { setLoading(false); }
    })();

    // Load user profile preferences (for listen mode, reveal, hands-free default)
    api.get("/users/me/prefs").then(({ data }) => {
      const p = data?.prefs || data || {};
      if (p.listen_mode) setListenMode(p.listen_mode);
      if (p.reveal_answer_mode) setRevealMode(p.reveal_answer_mode);
      if (p.hands_free_by_default) setHandsFree(true);
      if (typeof p.auto_hint_after_n === "number") setAutoHintAfterN(p.auto_hint_after_n);
      if (typeof p.cue_volume === "number") {
        try { localStorage.setItem("echo_cue_volume", String(p.cue_volume)); } catch {}
      }
      if (p.learn_mode_pause) setLearnPause(p.learn_mode_pause);
      if (typeof p.learn_script_repeat === "boolean") setLearnScriptRepeat(p.learn_script_repeat);
      if (typeof p.voice_commands_enabled === "boolean") setVoiceCommandsEnabled(p.voice_commands_enabled);
      if (typeof p.attempts_before_auto_reveal === "number") setAttemptsBeforeReveal(p.attempts_before_auto_reveal);
    }).catch(() => {});

    waitForVoices().then((vs) => {
      setVoices(vs);
      const saved = localStorage.getItem("echo_voice_uri");
      if (saved && vs.find((v) => v.voiceURI === saved)) setVoiceURI(saved);
      else {
        const preferred = vs.find((v) => /en[-_]/i.test(v.lang) && /female|samantha|karen|zira|google us english/i.test(v.name))
          || vs.find((v) => /en[-_]/i.test(v.lang));
        if (preferred) setVoiceURI(preferred.voiceURI);
      }
    });

    return () => {
      // Hard-stop EVERY path that could keep speaking after unmount:
      // (1) cancel any queued speech (flips the epoch in speech.js),
      // (2) bump the Learn-mode cancel token so the async runner aborts
      //     at its next isCancelled() check,
      // (3) flip pausedRef so the post-speak re-trigger setTimeout exits early,
      // (4) kill the recognizer. This is what keeps Ekkol from ghost-reading
      //     after you tap Exit or log out.
      try { cancelSpeech(); } catch {}
      try { learnCancelRef.current++; } catch {}
      try { pausedRef.current = true; } catch {}
      try { recRef.current?.stop(); } catch {}
    };
    // eslint-disable-next-line
  }, [deckId]);

  useEffect(() => { if (voiceURI) localStorage.setItem("echo_voice_uri", voiceURI); }, [voiceURI]);

  // --- Screen Wake Lock: keep phone/tablet screen awake during a session ---
  const wakeLockRef = useRef(null);
  const [wakeLockOn, setWakeLockOn] = useState(false);
  useEffect(() => {
    const request = async () => {
      try {
        if ("wakeLock" in navigator) {
          wakeLockRef.current = await navigator.wakeLock.request("screen");
          setWakeLockOn(true);
          wakeLockRef.current.addEventListener?.("release", () => setWakeLockOn(false));
        }
      } catch {
        // Silently ignore — Firefox older, low battery, or permission denied
      }
    };
    request();
    // Re-acquire if the tab returns to foreground (Wake Lock auto-releases on hidden)
    const onVisibility = () => {
      if (document.visibilityState === "visible" && !wakeLockRef.current) request();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      try { wakeLockRef.current?.release(); } catch {}
      wakeLockRef.current = null;
      setWakeLockOn(false);
    };
  }, []);

  // --- Core TTS reading ---
  const readCard = useCallback((c) => {
    if (!c) return;
    cancelSpeech();
    setIsSpeaking(true);
    const mode = modeRef.current.listenMode || "beep";

    // Barge-in: open mic right away so user can answer during/over the reading.
    if (mode === "bargein" && modeRef.current.handsFree && !pausedRef.current) {
      setTimeout(() => startListening(true), 100);
    }

    speak(textToRead(c), {
      rate,
      voiceURI: voiceURI || undefined,
      onend: () => {
        setIsSpeaking(false);
        if (!modeRef.current.handsFree || pausedRef.current) return;
        // In bargein mode, mic is already open — don't restart
        if (mode === "bargein") return;
        if (mode === "auto") {
          // Open mic silently, no beep
          setTimeout(() => startListening(true, { beep: false }), 150);
        } else {
          // Default "beep" — short audio cue then open mic
          cues.listenStart();
          setTimeout(() => startListening(true, { beep: false }), 320);
        }
      },
    });
    // eslint-disable-next-line
  }, [rate, voiceURI, isFlashcard]);

  const readCurrent = useCallback(() => readCard(cardsRef.current[idxRef.current]), [readCard]);

  // --- Recognition ---
  const startListening = useCallback((autoScore = false, opts = {}) => {
    const { beep = true } = opts;
    setTranscriptFinal("");
    setTranscriptInterim("");
    setMatchScore(null);
    // Sync mutation AND state update. The ref sync matters if the user
    // re-opens the mic very quickly after a miss — gradeAttempt's scoredRef
    // guard would otherwise still see the stale `false` from the previous
    // attempt and silently no-op.
    scoredRef.current = null;
    setScoredPass(null);
    setScoreReason("");

    // Local accumulator avoids a state→ref race where gradeAttempt() would
    // read transcriptFinalRef.current before React flushed the update.
    let finalAccum = "";
    let commandHandled = false;

    const runCommand = (cmd) => {
      if (cmd === "next") { advance(); return; }
      if (cmd === "prev") { prev(); return; }
      if (cmd === "repeat") { resetCardState(); readCurrent(); return; }
      if (cmd === "hint") { requestHint(); return; }
      if (cmd === "pause") { setPaused(true); toast("Paused. Tap Resume to continue."); return; }
      if (cmd === "stop") { endSession(); return; }
    };

    // Suppress voice-command interception when the current card's answer IS
    // one of the command words (e.g. a card whose answer is "Next" / "Hint"
    // / "Stop"). Without this, the user's correct spoken answer would fire
    // the matching voice command and either advance / pause / request a hint
    // instead of scoring their attempt. Applies only to flashcards (only they
    // have a target answer to compare against). We also let users bypass the
    // collision by saying the word prefixed with "command" — e.g. "command
    // next" still advances — but that's a future nicety, not shipped yet.
    const cardNow = cardsRef.current[idxRef.current];
    const answerNorm = (cardNow?.content || "").trim().toLowerCase();
    const altNorms = (cardNow?.alt_answers || []).map((a) => (a || "").trim().toLowerCase());
    const COMMAND_WORDS = new Set(["next", "previous", "back", "skip", "repeat", "again", "hint", "help", "clue", "pause", "wait", "stop", "end", "quit", "exit"]);
    const answerCollidesWithCommand =
      COMMAND_WORDS.has(answerNorm) ||
      altNorms.some((a) => COMMAND_WORDS.has(a));

    const rec = createRecognizer({
      onResult: ({ final, interim }) => {
        if (final) {
          finalAccum = (finalAccum + " " + final).trim();
          setTranscriptFinal(finalAccum);

          // Immediate voice-command takeover — fire as soon as we hear it
          // rather than waiting for the recognizer's onEnd debounce.
          // Respects the user's Profile toggle (voiceCommandsEnabledRef)
          // AND the per-card collision check (if this card's answer IS a
          // command word, score the answer rather than running the command).
          if (!commandHandled && voiceCommandsEnabledRef.current && !answerCollidesWithCommand) {
            const cmd = detectVoiceCommand(finalAccum);
            if (cmd) {
              commandHandled = true;
              try { recRef.current?.stop(); } catch {}
              setIsListening(false);
              runCommand(cmd);
              return;
            }
          }
        }
        setTranscriptInterim(interim || "");

        // Also scan interim for fast single-word commands like "next"/"stop"
        // so the user doesn't have to pause for finalization.
        if (!commandHandled && voiceCommandsEnabledRef.current && !answerCollidesWithCommand && interim) {
          const cmd = detectVoiceCommand(interim);
          if (cmd) {
            commandHandled = true;
            try { recRef.current?.stop(); } catch {}
            setIsListening(false);
            runCommand(cmd);
          }
        }
      },
      onEnd: () => {
        setIsListening(false);
        if (commandHandled) return;
        if (autoScore) {
          // pass finalAccum directly — no ref dependency. Invoke via the
          // gradeAttempt ref so we always run the LATEST version (fixes the
          // stale-closure bug where state captured at first mount — miss
          // counts, reveal threshold, autoHint — silently went stale).
          setTimeout(() => (gradeAttemptRef.current || (() => {}))(finalAccum), 60);
        }
      },
      onError: (e) => {
        setIsListening(false);
        if (e?.error !== "no-speech" && e?.error !== "aborted") {
          toast.error(`Mic: ${e?.error}`);
        }
        // Hands-free no-speech handling. The old behavior re-read the entire
        // question on every silent timeout, which was slow and — for very
        // short answers like "True" — could spin forever because the user
        // had already answered but the engine didn't capture audio. New
        // logic: after 2 silent cycles in a row, grade as empty (triggers
        // normal miss/hint/reveal flow) instead of re-reading yet again.
        if (modeRef.current.handsFree && !pausedRef.current && e?.error === "no-speech" && !commandHandled) {
          noSpeechStreakRef.current = (noSpeechStreakRef.current || 0) + 1;
          if (noSpeechStreakRef.current >= 2) {
            noSpeechStreakRef.current = 0;
            // Force a miss so the retry/hint/reveal logic takes over.
            setTimeout(() => (gradeAttemptRef.current || (() => {}))(""), 60);
          } else {
            setTimeout(() => readCurrent(), 400);
          }
        }
      },
    });
    if (!rec) {
      toast.error("Speech recognition unsupported. Try Chrome, Edge, or Safari.");
      return;
    }
    recRef.current = rec;
    try { rec.start(); setIsListening(true); if (beep) cues.listenStart(); } catch {}
  }, [readCurrent]);

  const stopListening = useCallback(() => {
    try { recRef.current?.stop(); } catch {}
    setIsListening(false);
  }, []);

  // gradeAttempt ref — keeps a pointer to the LATEST gradeAttempt closure so
  // the recognizer's onEnd (captured once inside startListening) always
  // invokes the fresh version. Without this, state captured in gradeAttempt
  // (attemptsBeforeReveal, deck, revealMode, autoHintAfterN…) goes stale and
  // miss-counting / reveal logic silently fails. Same pattern as missCountRef.
  const gradeAttemptRef = useRef(null);

  // Consecutive no-speech events. Bumped in the recognizer's onError path;
  // after 2 in a row we force an empty-transcript miss instead of re-reading
  // the question indefinitely. Resets on any successful capture (gradeAttempt
  // running with non-empty text) or card change.
  const noSpeechStreakRef = useRef(0);

  // --- Grading ---
  const gradeAttempt = useCallback(async (spokenArg) => {
    const c = cardsRef.current[idxRef.current];
    if (!c || scoredRef.current !== null) return;
    const spoken = (spokenArg ?? transcriptFinalRef.current ?? "").trim();
    // We USED to early-return on empty input and call readCurrent() in
    // hands-free, which created an infinite loop when the recognizer couldn't
    // capture audio (e.g. very short T/F answers where the mic barely opened).
    // Now: an empty transcript is treated as a 0% miss so the normal hint /
    // reveal pipeline takes over. The recognizer's onError damper bounds the
    // re-read attempts to 1 before this path fires anyway.
    // (Successful capture with content → reset the no-speech streak.)
    if (spoken) noSpeechStreakRef.current = 0;

    // Voice-command takeover (fallback — most commands are handled in onResult).
    // Skip entirely if the user has disabled voice commands in Profile OR if
    // the card's answer IS a command word (otherwise the correct answer
    // would trigger the command instead of scoring).
    const answerNormCmp = (c?.content || "").trim().toLowerCase();
    const altNormsCmp = (c?.alt_answers || []).map((a) => (a || "").trim().toLowerCase());
    const CMD_WORDS = new Set(["next", "previous", "back", "skip", "repeat", "again", "hint", "help", "clue", "pause", "wait", "stop", "end", "quit", "exit"]);
    const thisCardCollides = CMD_WORDS.has(answerNormCmp) || altNormsCmp.some((a) => CMD_WORDS.has(a));
    const cmd = (voiceCommandsEnabledRef.current && !thisCardCollides) ? detectVoiceCommand(spoken) : null;
    if (cmd) {
      if (cmd === "next") { advance(); return; }
      if (cmd === "prev") { prev(); return; }
      if (cmd === "repeat") { resetCardState(); readCurrent(); return; }
      if (cmd === "hint") { requestHint(); return; }
      if (cmd === "pause") { setPaused(true); toast("Paused. Tap Resume to continue."); return; }
      if (cmd === "stop") { endSession(); return; }
    }

    // Score
    let score = 0, passed = false, reason = "";
    const target = textToMatch(c);
    const altAnswers = Array.isArray(c?.alt_answers) ? c.alt_answers : [];
    // Empty transcript = certain miss; skip the AI round-trip entirely.
    if (!spoken) {
      score = 0;
      passed = false;
      reason = "no audio captured";
    } else if (matchMode === "concept") {
      try {
        const { data } = await api.post("/ai/match", {
          target, spoken, threshold: threshold,
          prompt: c?.prompt || null,
          alt_answers: altAnswers,
        });
        score = (data.score ?? 0) / 100;
        reason = data.reason || "";
        passed = !!data.passed;
      } catch {
        // Full target includes any card alternates so similarity() covers them
        const allTargets = [target, ...altAnswers].filter(Boolean).join(" | ");
        score = similarity(spoken, allTargets);
        passed = score * 100 >= threshold;
        reason = "concept engine unavailable — used word overlap";
      }
    } else {
      const allTargets = [target, ...altAnswers].filter(Boolean).join(" | ");
      score = similarity(spoken, allTargets);
      passed = score * 100 >= threshold;
    }

    setMatchScore(score);
    setScoredPass(passed);
    setScoreReason(reason);
    // Audio cue
    if (passed) cues.pass(); else cues.fail();
    api.post(`/decks/${deckId}/progress`, {
      card_id: c.card_id, correct: passed, transcript: spoken,
    }).catch(() => {});

    // SRS: record every graded attempt so the scheduler knows when to bring
    // this card back. Peeks are handled separately (in the Show Answer /
    // auto-reveal handlers) — this path only fires for actual spoken
    // attempts, passes OR misses.
    api.post(`/reviews/submit`, {
      deck_id: deckId,
      card_id: c.card_id,
      outcome: passed ? "pass" : "miss",
      score: passed ? score : null,
    }).catch(() => {});

    // Record session result (keep last entry per card)
    setSessionResults((prev) => {
      const next = prev.filter((r) => r.card_id !== c.card_id);
      next.push({
        card_id: c.card_id,
        content: c.content,
        prompt: c.prompt || null,
        passed, score: Math.round(score * 100), spoken,
      });
      return next;
    });
    if (!passed) {
      setFailedCards((prev) => (prev.find((x) => x.card_id === c.card_id) ? prev : [...prev, c]));
      // Track miss count for reveal-after-N logic. Read from the REF so we
      // always see the latest counter even when this callback is invoked from
      // a stale recognizer closure.
      const newMisses = (missCountRef.current[c.card_id] || 0) + 1;
      missCountRef.current = { ...missCountRef.current, [c.card_id]: newMisses };
      setMissCountByCard((m) => ({ ...m, [c.card_id]: newMisses }));

      // Should the auto-hint fire on THIS miss? (once per card)
      const willAutoHint =
        autoHintAfterN > 0 &&
        newMisses === autoHintAfterN &&  // exactly at threshold, not later
        !autoHintFiredRef.current.has(c.card_id);
      if (willAutoHint) {
        autoHintFiredRef.current.add(c.card_id);
      }

      // Flashcard flow: ANY card with a prompt is a flashcard here. The old
      // check `deck.mode === "qa" && deck.qa_style === "flashcard"` was
      // narrower and skipped the reveal logic for many real decks (the bug
      // the user hit — deck type was generic but cards had prompts). Simple
      // rule: if the current card has a prompt + answer, reveal logic applies.
      const cardIsFlashcard = !!c.prompt;
      if (cardIsFlashcard && !pausedRef.current) {
        const handsFreeNow = modeRef.current.handsFree;
        const revealThreshold = attemptsBeforeReveal; // 0 = never auto-reveal
        const shouldReveal = revealThreshold > 0 && newMisses >= revealThreshold;
        const cardId = c.card_id;
        setTimeout(() => {
          try { recRef.current?.stop(); } catch {}
          setIsListening(false);
          cancelSpeech();
          if (shouldReveal) {
            // Mark the card as revealed so the UI shows the answer, and
            // push it into the Retry pile — "cards you peeked at" belong
            // at the end-of-session review.
            setManualReveal(true);
            setRevealedCardIds((s) => { const n = new Set(s); n.add(cardId); return n; });
            // SRS: auto-reveal after N misses = user acknowledged they don't know it.
            api.post(`/reviews/submit`, { deck_id: deckId, card_id: cardId, outcome: "peek" }).catch(() => {});
            setSessionResults((prev) => prev.map((r) =>
              r.card_id === cardId ? { ...r, revealed: true } : r
            ));
            const msg = handsFreeNow
              ? `The correct answer is: ${target}. Moving to the next card.`
              : `The correct answer is: ${target}.`;
            speak(msg, {
              rate, voiceURI: voiceURI || undefined,
              onend: () => {
                if (pausedRef.current) return;
                if (handsFreeNow) {
                  setRetries(0);
                  setTimeout(() => advance(), 400);
                }
                // Non-hands-free: stay on card. User clicks Next when ready.
              },
            });
          } else if (willAutoHint && handsFreeNow) {
            // Hint turn: speak the hint, pause 2s, then re-read the question
            // and reopen the mic. The pause gives the user time to absorb
            // the hint before hearing the question again. Replaces the usual
            // "Not quite, try again" narration on this attempt — the hint IS
            // the coach's note.
            requestHint(2, { silent: true }).then((h) => {
              if (pausedRef.current) return;
              const hintText = (h || "").trim();
              const msg = hintText
                ? `Here's a hint: ${hintText}.`
                : "Let's try that again.";
              speak(msg, {
                rate, voiceURI: voiceURI || undefined,
                onend: () => {
                  if (pausedRef.current) return;
                  setRetries((r) => r + 1);
                  // 2-second beat between the hint and the re-read so the
                  // user can think. Uses a longer gap than normal retries.
                  setTimeout(() => {
                    if (pausedRef.current) return;
                    resetCardState();
                    // Keep the hint visible on screen after reset.
                    if (hintText) setCurrentHint(hintText);
                    readCurrent();
                  }, 2000);
                },
              });
            });
          } else if (handsFreeNow) {
            // Encouragement + auto-retry. No answer given yet — still N-1 tries left.
            const left = Math.max(0, revealThreshold - newMisses);
            const retryMsg = revealThreshold > 0
              ? (left === 1 ? "Not quite — one more try." : "Not quite — try again.")
              : "Not quite — try again.";
            speak(retryMsg, {
              rate, voiceURI: voiceURI || undefined,
              onend: () => {
                if (pausedRef.current) return;
                setRetries((r) => r + 1);
                setTimeout(() => { resetCardState(); readCurrent(); }, 400);
              },
            });
          }
          // Non-hands-free, still within threshold: fire the auto-hint
          // silently (text-only — user can see the screen) and increment
          // retries so the counter / "X tries left" countdown is accurate.
          if (!handsFreeNow && !shouldReveal) {
            if (willAutoHint) {
              requestHint(2, { silent: true });
            }
            setRetries((r) => r + 1);
          }
        }, 500);
      } else if (revealMode === "after3" && newMisses >= 3) {
        // Script mode reveal after 3 misses, read aloud + reopen mic
        setTimeout(() => {
          toast(`Answer: ${target}`, { duration: 4000 });
          if (modeRef.current.handsFree) {
            try { recRef.current?.stop(); } catch {}
            setIsListening(false);
            cancelSpeech();
            speak(`The correct answer is: ${target}`, {
              rate, voiceURI: voiceURI || undefined,
              onend: () => {
                if (!modeRef.current.handsFree || pausedRef.current) return;
                const mode = modeRef.current.listenMode || "beep";
                if (mode === "auto") {
                  setTimeout(() => startListening(true, { beep: false }), 200);
                } else {
                  cues.listenStart();
                  setTimeout(() => startListening(true, { beep: false }), 420);
                }
              },
            });
          } else {
            speak(`The correct answer is: ${target}`, { rate, voiceURI: voiceURI || undefined });
          }
        }, 500);
      }
    } else {
      setFailedCards((prev) => prev.filter((x) => x.card_id !== c.card_id));
    }

    // Auto-flow
    if (passed) {
      if (modeRef.current.autoAdvance || modeRef.current.handsFree) {
        setTimeout(() => advance(), 900);
      }
    } else if (c.prompt) {
      // Flashcard miss: the reveal-speech branch above chains retry/advance
      // via onend. Skip here so we don't race the reveal with readCurrent().
    } else if (modeRef.current.handsFree && !pausedRef.current) {
      const maxR = deck?.max_retries ?? 3;
      if (retriesRef.current + 1 < maxR) {
        setRetries((r) => r + 1);
        setTimeout(() => { resetCardState(); readCurrent(); }, 1100);
      } else {
        // give up and advance
        setRetries(0);
        setTimeout(() => advance(), 1400);
      }
    }
    // eslint-disable-next-line
  }, [matchMode, threshold, deckId, readCurrent, deck, revealMode, rate, voiceURI, autoHintAfterN, attemptsBeforeReveal]);

  const transcriptFinalRef = useRef("");
  useEffect(() => { transcriptFinalRef.current = transcriptFinal; }, [transcriptFinal]);
  // Keep the ref pointed at the CURRENT gradeAttempt instance on every render.
  // This is the breaker for the stale-closure trap in startListening/onEnd.
  useEffect(() => { gradeAttemptRef.current = gradeAttempt; }, [gradeAttempt]);

  // ---------------------------------------------------------------------
  // Test harness — window.__ekkolTest
  // Mounted only in non-production builds (CRA sets NODE_ENV='production'
  // during `yarn build`, so this entire block is dead code in the production
  // bundle). Gives the testing agent a way to drive the recognizer-grade
  // pipeline WITHOUT a real microphone:
  //   window.__ekkolTest.submit("wrong answer")     — invokes the CURRENT
  //                                                   gradeAttempt (via ref,
  //                                                   so it's never stale).
  //   window.__ekkolTest.state()                    — snapshot of counters,
  //                                                   hint, reveal, retries,
  //                                                   card, transcript.
  //   window.__ekkolTest.advance() / .prev() / .resetCard()
  // Detached on unmount. Nothing exposed here is privileged — every method
  // just invokes behaviour a real user could trigger through the UI — but we
  // still gate it behind NODE_ENV so production bundles stay minimal and
  // don't advertise an internal testing surface.
  // ---------------------------------------------------------------------
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (process.env.NODE_ENV === "production") return;
    window.__ekkolTest = {
      ready: true,
      submit: (text) => {
        const fn = gradeAttemptRef.current;
        if (typeof fn !== "function") return { ok: false, reason: "gradeAttempt not yet mounted" };
        try { recRef.current?.stop(); } catch {}
        // Simulate "user taps mic to retry" — clear previous scored state so
        // gradeAttempt's scoredRef guard doesn't no-op. Must mutate the ref
        // synchronously (React batching would delay the setState version).
        scoredRef.current = null;
        setScoredPass(null);
        setMatchScore(null);
        setScoreReason("");
        setTranscriptFinal(text);
        setTranscriptInterim("");
        return fn(text);
      },
      state: () => {
        const c = cardsRef.current[idxRef.current] || null;
        return {
          cardId: c?.card_id || null,
          idx: idxRef.current,
          prompt: c?.prompt || null,
          answer: c?.content || null,
          retries: retriesRef.current,
          missCount: c ? (missCountRef.current[c.card_id] || 0) : 0,
          manualReveal,
          hintLevel,
          currentHint,
          scoredPass,
          matchScore,
          attemptsBeforeReveal,
          autoHintAfterN,
          autoHintFired: c ? autoHintFiredRef.current.has(c.card_id) : false,
          handsFree,
          sessionMode,
          finished,
        };
      },
      advance: () => advance(),
      prev: () => prev(),
      resetCard: () => resetCardState(),
    };
    return () => { try { delete window.__ekkolTest; } catch {} };
    // eslint-disable-next-line
  }, [manualReveal, hintLevel, currentHint, scoredPass, matchScore, attemptsBeforeReveal, autoHintAfterN, handsFree, sessionMode, finished]);

  const advance = useCallback(() => {
    stopListening();
    cancelSpeech();
    setRetries(0);
    noSpeechStreakRef.current = 0;
    if (idxRef.current + 1 >= cardsRef.current.length) {
      // In Learn mode, the per-card runner would normally populate sessionResults
      // when it reaches the end. If the user manually taps Next to finish, populate
      // it here so the session counts toward the streak and daily goal.
      if (sessionMode === "learn") {
        setSessionResults((prev) => prev.length ? prev : cardsRef.current.map((c) => ({
          card_id: c.card_id, content: c.content, prompt: c.prompt, passed: null, score: null, spoken: "",
        })));
      }
      setFinished(true);
      return;
    }
    setIdx((i) => i + 1);
    resetCardState();
  }, [stopListening, sessionMode]);

  const prev = () => {
    stopListening();
    cancelSpeech();
    if (idx === 0) return;
    setIdx((i) => i - 1);
    resetCardState();
  };

  const repeat = () => {
    stopListening();
    resetCardState();
    cancelLearn();
    if (sessionMode === "learn") {
      setTimeout(() => { if (!pausedRef.current) runLearnCard(); }, 150);
    } else {
      setTimeout(readCurrent, 150);
    }
  };

  const resetCardState = () => {
    setTranscriptFinal(""); setTranscriptInterim("");
    setMatchScore(null); setScoredPass(null); setScoreReason("");
    setCurrentHint(""); setHintLevel(0);
    setManualReveal(false);
  };

  const requestHint = useCallback(async (nextLevel = null, opts = {}) => {
    const c = cardsRef.current[idxRef.current];
    if (!c || hintLoading) return;
    // Single-level hint (default: mid-depth). The old multi-level escalation
    // caused hands-free TTS race conditions — a level-2 hint's cancelSpeech
    // stomped on the "Not quite, try again" speech, stranding retries at 1.
    const lvl = nextLevel != null ? nextLevel : 2;
    // `silent: true` → just display the hint, don't speak it. Used by the
    // auto-hint path in gradeAttempt so it never interrupts in-flight speech.
    const silent = opts.silent === true;
    setHintLoading(true);
    try {
      const { data } = await api.post("/ai/hint", {
        target: c.content || "",
        prompt: c.prompt || null,
        original: c.original || null,
        mode: deck?.mode === "qa" ? "flashcard" : "script",
        level: lvl,
        miss_count: missCountRef.current[c.card_id] || 0,
      });
      const h = (data?.hint || "").trim();
      if (h) {
        setCurrentHint(h);
        setHintLevel(lvl);
        // MANUAL hint in hands-free: speak hint, pause 2s, re-read question,
        // then reopen mic. Same pattern as the auto-hint flow on miss-at-
        // threshold — feels unified no matter how the hint was triggered.
        if (!silent && modeRef.current.handsFree) {
          try { recRef.current?.stop(); } catch {}
          setIsListening(false);
          cancelSpeech();
          speak(`Here's a hint: ${h}.`, {
            rate,
            voiceURI: voiceURI || undefined,
            onend: () => {
              if (!modeRef.current.handsFree || pausedRef.current) return;
              // 2-second beat, then re-read the question. readCard handles
              // the "onend → open mic" handoff internally.
              setTimeout(() => {
                if (pausedRef.current) return;
                readCurrent();
              }, 2000);
            },
          });
        }
      }
      return h; // allow callers to chain on hint text (e.g. auto-hint flow)
    } catch (e) {
      console.error("Hint failed:", e);
      toast.error(e?.response?.data?.detail || "Hint unavailable");
      return "";
    } finally { setHintLoading(false); }
    // eslint-disable-next-line
  }, [hintLoading, deck, rate, voiceURI, readCurrent]);

  const endSession = () => {
    stopListening(); cancelSpeech();
    navigate(`/decks/${deckId}`);
  };

  // Retry only failed cards
  const retryFailed = () => {
    if (!failedCards.length) return;
    setCards(failedCards.slice()); // replace card pool with failed subset
    setIdx(0);
    setFailedCards([]);
    setSessionResults([]);
    setSummary("");
    setFinished(false);
    resetCardState();
  };

  // Fetch AI summary when session finishes + trigger Plus upsell (once per 5 sessions)
  useEffect(() => {
    if (!finished || !sessionResults.length || summary || summaryLoading) return;
    // Learn mode has no scoring context — skip coach's note fetch (saves AI call)
    if (sessionMode !== "learn") {
      setSummaryLoading(true);
      api.post("/ai/session-summary", { deck_id: deckId, results: sessionResults })
        .then(({ data }) => setSummary(data?.summary || ""))
        .catch(() => {})
        .finally(() => setSummaryLoading(false));
    }

    // Gamification: ping server to bump streak + daily progress
    api.post("/me/study-ping", { cards_studied: sessionResults.length, deck_id: deckId })
      .then(({ data }) => {
        if (data?.delta) setStreakDelta(data.delta);
      })
      .catch(() => {});

    // Gentle upsell logic: only Free users, only once every ~5 sessions, respects snooze
    try {
      const isFree = !user?.plan || user?.plan === "free";
      if (isFree && !user?.is_admin) {
        const snoozedUntil = parseInt(localStorage.getItem(UPSELL_DISMISS_KEY) || "0", 10);
        if (!snoozedUntil || snoozedUntil < Date.now()) {
          const count = (parseInt(localStorage.getItem(UPSELL_COUNTER_KEY) || "0", 10) || 0) + 1;
          localStorage.setItem(UPSELL_COUNTER_KEY, String(count));
          // Show on 1st session, then every 5th: 1, 6, 11, 16...
          if (count === 1 || count % 5 === 1) setShowUpsell(true);
        }
      }
    } catch {}
    // eslint-disable-next-line
  }, [finished]);

  // --- Learn mode runner (listen + pause + advance; no mic, no grading) ---
  const isFlashcardDeck = !!card?.prompt;
  const computePauseMs = useCallback((text) => {
    // Pauses must leave room for the user to REPEAT the line aloud if they want —
    // not just to hear it finish. Budget ≈ 95ms/char (reading aloud pace + think-time).
    if (learnPause === "short") return 3500;
    if (learnPause === "long") return 10000;
    if (learnPause === "auto") {
      const n = (text || "").length;
      // 95ms per character, minimum 4s (lets a short line like "Hello" breathe),
      // maximum 15s (long chunks still advance eventually).
      return Math.max(4000, Math.min(15000, n * 95));
    }
    return 6500; // medium — was 4000, bumped so it's actually usable for repeating aloud
  }, [learnPause]);

  // Learn-mode voice-command listener — ONLY runs during the silent pause phases
  // (we never listen while TTS plays to avoid the mic hearing Ekkol's own voice).
  // Recognized commands: next, repeat, pause, stop, back (voice-only; tap controls
  // always work too). When a command fires, resolves with the command string.
  // If the user has disabled voice commands in Profile, this becomes a plain timer.
  const listenDuringPause = useCallback((ms) => new Promise((resolve) => {
    if (!voiceCommandsEnabledRef.current) {
      setTimeout(() => resolve(null), ms);
      return;
    }
    let resolved = false;
    const done = (cmd = null) => { if (!resolved) { resolved = true; rec?.abort?.(); clearTimeout(t); resolve(cmd); } };
    const rec = createRecognizer({
      onResult: ({ final, interim }) => {
        const txt = (final || interim || "").toLowerCase().trim();
        if (!txt) return;
        if (/\b(next|skip|forward)\b/.test(txt)) return done("next");
        if (/\b(repeat|again|once more|say it again)\b/.test(txt)) return done("repeat");
        if (/\b(pause|wait|hold on)\b/.test(txt)) return done("pause");
        if (/\b(stop|end|quit|exit)\b/.test(txt)) return done("stop");
        if (/\b(back|previous|prev)\b/.test(txt)) return done("back");
      },
      onEnd: () => { /* resolve on timeout instead */ },
      onError: () => { /* ignore, pause will still complete on timeout */ },
      continuous: true,
    });
    if (rec) {
      try { rec.start(); } catch { /* already started in rare race */ }
    }
    const t = setTimeout(() => done(null), ms);
  }), []);

  const runLearnCard = useCallback(async () => {
    if (!card) return;
    // Guard against post-unmount / post-pause re-entry: if the component has
    // been torn down (cleanup sets pausedRef=true), bail BEFORE firing any
    // new TTS. This prevents a ghost-reader loop after Exit / logout.
    if (pausedRef.current) return;
    const myToken = ++learnCancelRef.current;
    const isCancelled = () => learnCancelRef.current !== myToken || pausedRef.current;
    const speakP = (text, opts = {}) => new Promise((resolve) => {
      if (!text || !text.trim()) return resolve();
      setIsSpeaking(true);
      speak(text, {
        rate, voiceURI: voiceURI || undefined, ...opts,
        onend: () => { setIsSpeaking(false); resolve(); },
      });
    });
    // Wait AND listen for voice commands during the silent pause.
    // Returns the command ("next", "repeat", "back", "pause", "stop") or null.
    const waitWithCommands = (ms) => listenDuringPause(ms);

    const handleCommand = (cmd) => {
      if (!cmd) return false; // no command — continue normally
      if (cmd === "pause" || cmd === "stop") {
        pausedRef.current = true;
        setPaused(true);
        toast(cmd === "stop" ? "Stopped. Tap Play to resume." : "Paused. Tap Play to resume.", { duration: 2200 });
        return true;
      }
      if (cmd === "next") {
        // fall through — end the card and advance
        return "next";
      }
      if (cmd === "back") {
        return "back";
      }
      if (cmd === "repeat") {
        return "repeat";
      }
      return false;
    };

    const isFlashcard = !!card.prompt;
    let loopedBack = false;
    let gotoPrev = false;

    // Speak prompt (or chunk for script decks)
    const firstText = isFlashcard ? (card.prompt || "") : (card.content || "");
    await speakP(firstText);
    if (isCancelled()) return;
    let cmd = await waitWithCommands(computePauseMs(firstText));
    if (isCancelled()) return;
    let action = handleCommand(cmd);
    if (action === "repeat") {
      loopedBack = true; // re-run this card
    } else if (action === "back") {
      gotoPrev = true;
    } else if (action === true) {
      return; // paused/stopped
    } else if (action !== "next") {
      // Continue with the second phase (answer or re-read)
      if (isFlashcard) {
        await speakP(card.content || "");
        if (isCancelled()) return;
        cmd = await waitWithCommands(computePauseMs(card.content || ""));
        if (isCancelled()) return;
        action = handleCommand(cmd);
        if (action === "repeat") { loopedBack = true; }
        else if (action === "back") { gotoPrev = true; }
        else if (action === true) { return; }
      } else if (learnScriptRepeat) {
        await speakP(firstText, { rate: Math.max(0.7, rate - 0.1) });
        if (isCancelled()) return;
        cmd = await waitWithCommands(computePauseMs(firstText));
        if (isCancelled()) return;
        action = handleCommand(cmd);
        if (action === "repeat") { loopedBack = true; }
        else if (action === "back") { gotoPrev = true; }
        else if (action === true) { return; }
      }
    }

    if (isCancelled()) return;
    if (loopedBack) {
      // Re-trigger the same card by bumping a dummy state — reuse idx
      setIdx((n) => n); // no-op, but effect re-runs via the cancel token bump below
      learnCancelRef.current++; // force the effect to re-invoke via idx effect
      // Easiest: schedule re-run of this card by pausing briefly.
      // Gate on pausedRef so a fast Exit / unmount can't trigger another TTS burst.
      setTimeout(() => { if (!pausedRef.current) runLearnCard(); }, 50);
      return;
    }
    if (gotoPrev) {
      if (idxRef.current > 0) setIdx((n) => n - 1);
      return;
    }
    // Advance to next card (or finish)
    if (idxRef.current + 1 < cards.length) {
      setIdx((n) => n + 1);
    } else {
      setSessionResults((prev) => prev.length ? prev : cards.map((c) => ({
        card_id: c.card_id, content: c.content, prompt: c.prompt, passed: null, score: null, spoken: "",
      })));
      setFinished(true);
    }
    // eslint-disable-next-line
  }, [card, cards, learnScriptRepeat, computePauseMs, rate, voiceURI, listenDuringPause]);

  // Cancel any in-flight learn loop when mode flips, paused, or navigation
  const cancelLearn = useCallback(() => {
    learnCancelRef.current++;
    cancelSpeech();
    setIsSpeaking(false);
  }, []);

  // Auto-read when card changes (or when hands-free starts)
  useEffect(() => {
    if (!card || finished) return;
    // Block auto-play while any first-time prep overlay is showing
    if (showPracticePrep || showLearnPrep) return;
    if (sessionMode === "learn") {
      // In Learn mode, use the listen+repeat runner instead of grading flow
      try { recRef.current?.stop(); } catch {}
      setIsListening(false);
      const t = setTimeout(() => { if (!pausedRef.current) runLearnCard(); }, 250);
      return () => { clearTimeout(t); cancelLearn(); };
    }
    const t = setTimeout(() => readCurrent(), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [idx, card?.card_id, finished, sessionMode, learnPause, learnScriptRepeat, showPracticePrep, showLearnPrep]);

  // When hands-free toggled on, kick off loop
  useEffect(() => {
    if (sessionMode === "learn") return; // Learn mode handles its own loop
    if (showPracticePrep || showLearnPrep) return; // wait for user to dismiss the guide
    if (handsFree && !isSpeaking && !isListening && !paused && card) {
      readCurrent();
    }
    if (!handsFree) {
      stopListening();
    }
    // eslint-disable-next-line
  }, [handsFree, sessionMode, showPracticePrep, showLearnPrep]);

  const progressPct = cards.length ? Math.round(((idx + 1) / cards.length) * 100) : 0;

  if (loading || !card) {
    return (
      <div className="min-h-screen bg-[color:var(--paper)]"><Navbar/>
        <div className="max-w-3xl mx-auto py-20 px-6"><div className="wave"><span/><span/><span/><span/><span/></div></div>
      </div>
    );
  }

  if (finished) {
    const total = sessionResults.length;
    const passed = sessionResults.filter((r) => r.passed).length;
    const accuracy = total ? Math.round((passed / total) * 100) : 0;
    const isLearnSession = sessionMode === "learn";
    return (
      <div className="min-h-screen bg-[color:var(--paper)]"><Navbar/>
        <main className="max-w-2xl mx-auto px-6 py-20 fade-in" data-testid="session-complete">
          <div className="text-center">
            <div className="eyebrow mb-4">{isLearnSession ? "Learning session complete" : "Session complete"}</div>
            <h1 className="font-serif text-5xl tracking-tight leading-none">Well rehearsed.</h1>
            {isLearnSession && (
              <p className="text-[color:var(--ink-muted)] mt-4 max-w-md mx-auto leading-relaxed">
                Nice work — you listened all the way through. Learn mode plants the words; the real reinforcement happens when you say them back. You got this.
              </p>
            )}
          </div>

          {/* Streak / milestone banner — only when we have new data back from the server */}
          {streakDelta && streakDelta.streak > 0 && (
            <div
              className="mt-8 border rule bg-white p-5 flex items-center justify-between gap-4 fade-in"
              data-testid="streak-banner"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-orange-50 flex items-center justify-center">
                  <Flame size={22} weight="fill" className="text-orange-500" />
                </div>
                <div>
                  <div className="eyebrow">
                    {streakDelta.milestone ? "Milestone!" : streakDelta.freeze_consumed ? "Streak freeze used" : "Streak kept"}
                  </div>
                  <div className="font-serif text-2xl leading-tight">
                    {streakDelta.streak} day{streakDelta.streak === 1 ? "" : "s"} in a row
                  </div>
                  {streakDelta.earned_freeze && (
                    <p className="text-xs text-[color:var(--ink-muted)] mt-1 flex items-center gap-1">
                      <Snowflake size={12} weight="fill" className="text-sky-500" />
                      You earned a streak freeze — we&apos;ll auto-use it if you miss a day.
                    </p>
                  )}
                  {streakDelta.milestone && !streakDelta.earned_freeze && (
                    <p className="text-xs text-[color:var(--ink-muted)] mt-1 flex items-center gap-1">
                      <Trophy size={12} weight="fill" className="text-amber-500" />
                      Nice work — consistency beats cramming.
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {isLearnSession ? (
            /* Learn-mode: celebrate completion, no scoring shame. */
            <div
              className="grid grid-cols-2 sm:grid-cols-3 gap-px bg-[color:var(--rule)] border rule mt-10"
              data-testid="learn-complete-summary"
            >
              <div className="bg-white p-6 text-center">
                <div className="eyebrow">Cards reviewed</div>
                <div className="font-mono text-3xl mt-1">{total}</div>
              </div>
              <div className="bg-white p-6 text-center">
                <div className="eyebrow">Mode</div>
                <div className="font-serif text-2xl mt-1 flex items-center justify-center gap-2">
                  <Headphones size={20} weight="duotone"/> Learn
                </div>
              </div>
              <div className="bg-white p-6 text-center col-span-2 sm:col-span-1">
                <div className="eyebrow">Ready for</div>
                <div className="font-serif text-2xl mt-1 flex items-center justify-center gap-2">
                  <Microphone size={20} weight="duotone"/> Practice
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className={`grid ${revealedCardIds.size > 0 ? "grid-cols-2 sm:grid-cols-4" : "grid-cols-3"} gap-px bg-[color:var(--rule)] border rule mt-10`}>
                <div className="bg-white p-6 text-center">
                  <div className="eyebrow">Cards</div>
                  <div className="font-mono text-3xl mt-1">{total}</div>
                </div>
                <div className="bg-white p-6 text-center">
                  <div className="eyebrow">Passed</div>
                  <div className="font-mono text-3xl mt-1 text-[color:var(--success)]">{passed}</div>
                </div>
                <div className="bg-white p-6 text-center">
                  <div className="eyebrow">Accuracy</div>
                  <div className="font-mono text-3xl mt-1">{accuracy}%</div>
                </div>
                {revealedCardIds.size > 0 && (
                  <div className="bg-white p-6 text-center" data-testid="summary-peeked">
                    <div className="eyebrow">Peeked</div>
                    <div className="font-mono text-3xl mt-1 text-[color:var(--oxford)]">{revealedCardIds.size}</div>
                    <div className="text-[10px] text-[color:var(--ink-faint)] font-mono uppercase tracking-widest mt-1">in retry pile</div>
                  </div>
                )}
              </div>
              {revealedCardIds.size > 0 && (
                <p className="mt-3 text-xs text-[color:var(--ink-muted)] text-center italic" data-testid="summary-peeked-note">
                  You revealed {revealedCardIds.size} of {total} {revealedCardIds.size === 1 ? "card" : "cards"} — those are the ones you don&apos;t quite know yet. They&apos;re queued in your Retry pile.
                </p>
              )}
            </>
          )}

          {/* Coach's note — only shown for Practice mode (Learn has no grading context to coach on) */}
          {!isLearnSession && (
            <div className="mt-8 border rule bg-white p-6">
              <div className="eyebrow mb-3">Coach&apos;s note</div>
              {summaryLoading ? (
                <div className="wave"><span/><span/><span/><span/><span/></div>
              ) : summary ? (
                <p className="font-serif text-lg leading-relaxed" data-testid="session-summary">{summary}</p>
              ) : (
                /* Local fallback when the AI coach's note is unavailable.
                   Derived entirely from the session's results — never leaves
                   the user with a dead-end "summary unavailable" message. */
                <p className="font-serif text-lg leading-relaxed" data-testid="session-summary-fallback">
                  {accuracy === 100 && revealedCardIds.size === 0
                    ? `Clean run — you passed all ${total} ${total === 1 ? "card" : "cards"}. Come back tomorrow to keep it locked in.`
                    : accuracy >= 80
                      ? `Strong session — ${passed} of ${total} passed (${accuracy}%)${revealedCardIds.size > 0 ? `, ${revealedCardIds.size} peeked` : ""}. A short retry pass on the misses will cement it.`
                      : accuracy >= 50
                        ? `Solid practice — ${passed} of ${total} passed (${accuracy}%). The cards you missed${revealedCardIds.size > 0 ? ` or peeked at (${revealedCardIds.size})` : ""} are ready in the retry pile.`
                        : `${passed} of ${total} passed (${accuracy}%). This material is still new to you — try Learn mode first to absorb it before testing again.`}
                </p>
              )}
            </div>
          )}

          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link to={`/decks/${deckId}`} className="btn-ghost" data-testid="btn-back-to-deck">Back to deck</Link>
            {isLearnSession ? (
              <>
                <button
                  onClick={() => {
                    setSessionMode("practice");
                    api.get(`/decks/${deckId}`).then(({ data }) => {
                      setCards(data.cards);
                      setIdx(0); setFailedCards([]); setSessionResults([]); setSummary("");
                      setStreakDelta(null); setFinished(false); resetCardState();
                      setRevealedCardIds(new Set()); setMissCountByCard({}); missCountRef.current = {};
                      autoHintFiredRef.current = new Set();
                    });
                  }}
                  className="btn-primary"
                  data-testid="btn-try-practice-mode"
                >
                  <Microphone size={16} weight="fill"/> Try it in Practice mode
                </button>
                <button
                  onClick={() => {
                    api.get(`/decks/${deckId}`).then(({ data }) => {
                      setCards(data.cards);
                      setIdx(0); setFailedCards([]); setSessionResults([]); setSummary("");
                      setStreakDelta(null); setFinished(false); resetCardState();
                      setRevealedCardIds(new Set()); setMissCountByCard({}); missCountRef.current = {};
                      autoHintFiredRef.current = new Set();
                    });
                  }}
                  className="btn-ghost"
                  data-testid="restart-session"
                >
                  <ArrowClockwise size={16}/> Listen again
                </button>
              </>
            ) : (
              <>
                {failedCards.length > 0 && (
                  <button onClick={retryFailed} className="btn-primary" data-testid="retry-failed-btn">
                    <ArrowClockwise size={16}/> Retry {failedCards.length} missed {failedCards.length === 1 ? "card" : "cards"}
                  </button>
                )}
                <button onClick={() => {
                  // full restart with original deck cards
                  api.get(`/decks/${deckId}`).then(({ data }) => {
                    setCards(data.cards);
                    setIdx(0); setFailedCards([]); setSessionResults([]); setSummary("");
                    setStreakDelta(null); setFinished(false); resetCardState();
                    setRevealedCardIds(new Set()); setMissCountByCard({}); missCountRef.current = {};
                    autoHintFiredRef.current = new Set();
                  });
                }} className="btn-ghost" data-testid="restart-session">
                  <ArrowClockwise size={16}/> Go again — full deck
                </button>
              </>
            )}
          </div>

          {/* Post-session ad slot — free users only, never during study */}
          <AdSlot placement="post-session" />

          {/* Gentle Plus upsell — Free users only, once per ~5 sessions */}
          {showUpsell && (
            <div
              className="mt-10 border rule bg-white p-5 flex items-start gap-4 fade-in"
              data-testid="plus-upsell"            >
              <div className="w-10 h-10 rounded-full bg-[color:var(--oxford)]/10 flex items-center justify-center flex-shrink-0">
                <Crown size={18} weight="duotone" className="text-[color:var(--oxford)]"/>
              </div>
              <div className="flex-1">
                <div className="eyebrow mb-1">One more thing</div>
                <p className="text-sm text-[color:var(--ink)] leading-relaxed">
                  You&apos;re on the free plan. <strong>Ekkol Plus</strong> removes the daily limits — unlimited decks, 300 hints/day instead of 15, Smart Review scheduling (coming soon), and premium voices. <span className="text-[color:var(--ink-muted)]">$7.99/mo or $59/yr · cancel anytime.</span>
                </p>
                <div className="mt-3 flex items-center gap-3 flex-wrap">
                  <Link to="/pricing" className="btn-primary py-1.5 px-3 text-xs" data-testid="btn-upsell-see-plus">
                    <Sparkle size={12} weight="fill"/> See Plus
                  </Link>
                  <button
                    onClick={() => {
                      // Snooze for 7 days
                      try { localStorage.setItem(UPSELL_DISMISS_KEY, String(Date.now() + 7*24*3600*1000)); } catch {}
                      setShowUpsell(false);
                    }}
                    className="text-xs link-underline text-[color:var(--ink-muted)]"
                    data-testid="btn-upsell-snooze"
                  >
                    Not now
                  </button>
                </div>
              </div>
              <button
                onClick={() => setShowUpsell(false)}
                className="text-[color:var(--ink-faint)] hover:text-[color:var(--ink)]"
                aria-label="Close"
              >
                <X size={16}/>
              </button>
            </div>
          )}
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[color:var(--paper)] flex flex-col">
      <Navbar />

      {/* First-time Practice-mode prep overlay */}
      {showPracticePrep && (
        <div
          className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-start sm:items-center justify-center fade-in overflow-y-auto"
          onClick={(e) => { if (e.target === e.currentTarget) dismissPracticePrep(false); }}
          data-testid="prep-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="prep-overlay-title"
          style={{
            // Android/iOS web view: dialog used to clip the "Got it" button
            // off the bottom of the screen because we anchored center-only and
            // never reserved room for the OS chrome. Now we top-align on
            // mobile, allow body scroll, and pad both safe-area edges so the
            // dismiss button is always reachable.
            paddingTop: "max(env(safe-area-inset-top, 0px), 16px)",
            paddingBottom: "max(env(safe-area-inset-bottom, 0px), 16px)",
            paddingLeft: "16px",
            paddingRight: "16px",
          }}
        >
          <div className="bg-white border rule w-full max-w-lg my-auto">
            <div className="flex items-start justify-between gap-4 border-b rule px-6 py-5">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-[color:var(--oxford)]/10 flex items-center justify-center mt-1">
                  <BookOpen size={18} weight="duotone" className="text-[color:var(--oxford)]"/>
                </div>
                <div>
                  <div className="eyebrow">Before you begin</div>
                  <h2 id="prep-overlay-title" className="font-serif text-2xl leading-tight mt-1">A quick note on how Ekkol works</h2>
                </div>
              </div>
              <button onClick={() => dismissPracticePrep(false)} aria-label="Close" className="text-[color:var(--ink-muted)] hover:text-[color:var(--ink)]">
                <X size={20}/>
              </button>
            </div>
            <div className="px-6 py-5 space-y-4 text-sm leading-relaxed text-[color:var(--ink-muted)]">
              <p className="text-[color:var(--ink)]">
                Ekkol helps you <strong>remember</strong> material you&apos;ve already learned — it&apos;s not a replacement for the textbook, the lecture, or your teacher.
              </p>
              <div className="border-l-2 border-[color:var(--oxford)] pl-4 space-y-2">
                <p className="font-mono text-xs uppercase tracking-widest text-[color:var(--oxford)]">The best routine</p>
                <ul className="space-y-1.5">
                  <li>📖 <strong>First</strong>, study the material in class, your book, or your notes. Understand what it <em>means</em>.</li>
                  <li>🎧 <strong>Then</strong>, come here and practice — short sessions (5-10 min) beat one long cram.</li>
                  <li>💡 <strong>Stuck?</strong> Tap the hint button — small clues, not the full answer.</li>
                  <li>🔁 <strong>Miss a card?</strong> At the end, tap &ldquo;Retry missed cards&rdquo; — locks them in before you forget.</li>
                  <li>🎙️ <strong>Hands-free?</strong> While the mic is on, you can say &ldquo;next&rdquo;, &ldquo;previous&rdquo;, &ldquo;repeat&rdquo;, &ldquo;hint&rdquo;, &ldquo;pause&rdquo;, or &ldquo;stop&rdquo; to control the session. Turn this off anytime in <Link to="/profile" className="link-underline">Profile</Link>.</li>
                </ul>
              </div>
              <div className="border rule bg-[color:var(--surface-alt)] p-3">
                <p className="text-xs">
                  🎧 <strong>Want zero pressure?</strong> Switch to <strong>Learn mode</strong> (pill at the top) — Ekkol reads everything to you with pauses. No mic, no scoring. Perfect for the first few times through new material.
                </p>
              </div>
              <p className="text-xs text-[color:var(--ink-faint)]">
                You can re-open this guide anytime by tapping the <span className="font-mono">?</span> icon near the mode switcher.
              </p>
            </div>
            <div className="flex items-center justify-between gap-3 border-t rule px-6 py-4">
              <button onClick={() => dismissPracticePrep(false)} className="text-xs link-underline text-[color:var(--ink-muted)]" data-testid="btn-show-prep-again">
                Close — remind me next time
              </button>
              <button onClick={() => dismissPracticePrep(true)} className="btn-primary" data-testid="btn-prep-got-it">
                <Sparkle size={14} weight="fill"/> Got it — don&apos;t show again
              </button>
            </div>
          </div>
        </div>
      )}

      {/* First-time Learn-mode prep overlay */}
      {showLearnPrep && (
        <div
          className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-start sm:items-center justify-center fade-in overflow-y-auto"
          onClick={(e) => { if (e.target === e.currentTarget) dismissLearnPrep(false); }}
          data-testid="learn-prep-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="learn-prep-title"
          style={{
            paddingTop: "max(env(safe-area-inset-top, 0px), 16px)",
            paddingBottom: "max(env(safe-area-inset-bottom, 0px), 16px)",
            paddingLeft: "16px",
            paddingRight: "16px",
          }}
        >
          <div className="bg-white border rule w-full max-w-lg my-auto">
            <div className="flex items-start justify-between gap-4 border-b rule px-6 py-5">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-[color:var(--oxford)]/10 flex items-center justify-center mt-1">
                  <Headphones size={18} weight="duotone" className="text-[color:var(--oxford)]"/>
                </div>
                <div>
                  <div className="eyebrow">Learn mode</div>
                  <h2 id="learn-prep-title" className="font-serif text-2xl leading-tight mt-1">Listen &amp; repeat — no pressure</h2>
                </div>
              </div>
              <button onClick={() => dismissLearnPrep(false)} aria-label="Close" className="text-[color:var(--ink-muted)] hover:text-[color:var(--ink)]">
                <X size={20}/>
              </button>
            </div>
            <div className="px-6 py-5 space-y-4 text-sm leading-relaxed text-[color:var(--ink-muted)]">
              <p className="text-[color:var(--ink)]">
                In Learn mode, Ekkol reads each card to you, pauses so you can repeat (in your head or out loud), then moves to the next card automatically. <strong>No mic, no scoring, no pressure.</strong>
              </p>
              <div className="border-l-2 border-[color:var(--oxford)] pl-4 space-y-2">
                <p className="font-mono text-xs uppercase tracking-widest text-[color:var(--oxford)]">Key controls</p>
                <ul className="space-y-1.5">
                  <li>⏱️ <strong>Pause length</strong> — in the top banner, set Short (3.5s), Medium (6.5s), Long (10s), or Auto (scales with text length, 4-15s). If the reading feels too fast, make the pause longer.</li>
                  <li>⏯️ <strong>Play / Pause</strong> — the big round button stops the reading mid-card. Tap again to resume.</li>
                  <li>⏭️ <strong>Next</strong> — skip ahead anytime.</li>
                  <li>🔁 <strong>Repeat</strong> — re-read the current card.</li>
                  <li>🎙️ <strong>Hands-free?</strong> You can also <em>say</em> &ldquo;next&rdquo;, &ldquo;back&rdquo;, &ldquo;repeat&rdquo;, &ldquo;pause&rdquo;, or &ldquo;stop&rdquo; during the pause between reads. Ekkol only listens when it&apos;s not speaking — no feedback loops. Turn this off anytime in <Link to="/profile" className="link-underline">Profile</Link>.</li>
                </ul>
              </div>
              <div className="border rule bg-[color:var(--surface-alt)] p-3 space-y-2">
                <p className="text-xs"><strong>Your default pause right now:</strong></p>
                <div className="grid grid-cols-4 gap-px bg-[color:var(--rule)] border rule">
                  {[
                    { k: "short", label: "Short 3.5s" },
                    { k: "medium", label: "Medium 6.5s" },
                    { k: "long", label: "Long 10s" },
                    { k: "auto", label: "Auto" },
                  ].map((m) => (
                    <button
                      key={m.k}
                      onClick={() => setLearnPause(m.k)}
                      className={`p-2 text-center transition-colors ${learnPause === m.k ? "bg-[color:var(--ink)] text-white font-semibold" : "bg-white opacity-70 hover:opacity-100"}`}
                      data-testid={`learn-prep-pause-${m.k}`}
                    >
                      <span className="font-mono text-[10px] uppercase tracking-widest">{m.label}</span>
                    </button>
                  ))}
                </div>
                <p className="text-[11px] text-[color:var(--ink-faint)]">
                  Pick one and tap <em>Save as my default</em> to apply it everywhere. You can always change it later in <Link to="/profile" className="link-underline font-medium">Profile</Link>.
                </p>
              </div>
              <p className="text-xs text-[color:var(--ink-faint)]">
                Re-open this guide anytime by tapping the <span className="font-mono">?</span> icon near the mode switcher.
              </p>
            </div>
            <div className="flex items-center justify-between gap-3 border-t rule px-6 py-4 flex-wrap">
              <button onClick={() => dismissLearnPrep(false)} className="text-xs link-underline text-[color:var(--ink-muted)]" data-testid="btn-learn-prep-dismiss">
                Close — remind me next time
              </button>
              <div className="flex items-center gap-2">
                <button
                  onClick={async () => {
                    try {
                      await api.patch("/users/me", {
                        learn_mode_pause: learnPause,
                        learn_script_repeat: learnScriptRepeat,
                      });
                      toast.success("Saved as your default");
                    } catch {
                      toast.error("Could not save — try again from Profile");
                    }
                    dismissLearnPrep(true);
                  }}
                  className="btn-ghost py-2 px-3 text-xs"
                  data-testid="btn-learn-prep-save-default"
                >
                  <GearSix size={12}/> Save as my default
                </button>
                <button onClick={() => dismissLearnPrep(true)} className="btn-primary" data-testid="btn-learn-prep-start">
                  <Sparkle size={14} weight="fill"/> Got it — don&apos;t show again
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Top bar */}
      <div className="border-b rule bg-white/80 backdrop-blur-xl">
        <div className="max-w-4xl mx-auto px-6 py-3 flex items-center justify-between flex-wrap gap-3">
          <Link to={`/decks/${deckId}`} className="text-sm link-underline inline-flex items-center gap-1" data-testid="btn-exit">
            <ArrowLeft size={14}/> Exit
          </Link>
          <div className="eyebrow" data-testid="session-progress">
            {String(idx+1).padStart(2,"0")} / {String(cards.length).padStart(2,"0")} · {progressPct}%
            {wakeLockOn && (
              <span
                className="ml-3 text-[color:var(--success)] normal-case tracking-normal"
                title="Screen will stay awake during this session"
                data-testid="wake-lock-indicator"
              >
                · screen awake
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            {/* Mode pill: Practice / Learn */}
            <div className="inline-flex border rule bg-white overflow-hidden" data-testid="mode-switch">
              <button
                onClick={() => {
                  if (sessionMode === "practice") return;
                  toast(`Switch to Practice mode?`, {
                    duration: 10000,
                    action: {
                      label: "Restart",
                      onClick: () => {
                        cancelLearn();
                        setSessionMode("practice");
                        setIdx(0); setFailedCards([]); setSessionResults([]); resetCardState();
                      },
                    },
                    cancel: {
                      label: "Continue here",
                      onClick: () => {
                        cancelLearn();
                        setSessionMode("practice");
                        resetCardState();
                      },
                    },
                  });
                }}
                className={`text-[10px] font-mono uppercase tracking-[0.2em] px-3 py-1.5 transition-colors ${sessionMode === "practice" ? "bg-[color:var(--ink)] text-white" : "hover:bg-[color:var(--surface-alt)]"}`}
                data-testid="btn-mode-practice"
              >
                Practice
              </button>
              <button
                onClick={() => {
                  if (sessionMode === "learn") return;
                  toast(`Switch to Learn mode?`, {
                    duration: 10000,
                    action: {
                      label: "Restart",
                      onClick: () => {
                        try { recRef.current?.stop(); } catch {}
                        setIsListening(false);
                        cancelSpeech();
                        setSessionMode("learn");
                        setIdx(0); setFailedCards([]); setSessionResults([]); resetCardState();
                      },
                    },
                    cancel: {
                      label: "Continue here",
                      onClick: () => {
                        try { recRef.current?.stop(); } catch {}
                        setIsListening(false);
                        cancelSpeech();
                        setSessionMode("learn");
                        resetCardState();
                      },
                    },
                  });
                }}
                className={`text-[10px] font-mono uppercase tracking-[0.2em] px-3 py-1.5 border-l rule transition-colors ${sessionMode === "learn" ? "bg-[color:var(--ink)] text-white" : "hover:bg-[color:var(--surface-alt)]"}`}
                data-testid="btn-mode-learn"
              >
                Learn
              </button>
            </div>
            <button
              onClick={() => setShowModeHelp(true)}
              className="text-[color:var(--ink-muted)] hover:text-[color:var(--ink)] text-xs font-mono"
              aria-label="What do these modes mean?"
              title="What do these modes mean?"
              data-testid="btn-mode-help"
            >
              ?
            </button>
            {sessionMode === "practice" && (
              <button
                onClick={() => setHandsFree((h) => !h)}
                className={`text-xs font-mono uppercase tracking-[0.2em] px-3 py-1.5 border rule transition-colors inline-flex items-center gap-1 ${handsFree ? "bg-[color:var(--ink)] text-white border-[color:var(--ink)]" : "bg-white hover:border-[color:var(--ink)]"}`}
                data-testid="btn-handsfree-toggle"
              >
                {handsFree && <CheckCircle size={12} weight="fill"/>}
                <Headphones size={12}/> Hands-free {handsFree ? "ON" : "OFF"}
              </button>
            )}
            <button onClick={() => setShowSettings((s) => !s)} className="text-[color:var(--ink-muted)] hover:text-[color:var(--ink)] inline-flex items-center gap-1 text-xs" aria-label="Voice &amp; settings" data-testid="btn-settings" title="Voice, volume, hints, and other session settings">
              <SpeakerHigh size={14}/>
              <span className="hidden md:inline font-mono uppercase tracking-widest text-[10px]">
                {voiceURI
                  ? (voices.find((v) => v.voiceURI === voiceURI)?.name?.split(" ")[0] || "Voice")
                  : "Voice"}
              </span>
              <GearSix size={14}/>
            </button>
          </div>
        </div>
        <div className="h-[3px] bg-[color:var(--surface-alt)]">
          <div className="h-full bg-[color:var(--ink)] transition-all duration-500" style={{width: `${progressPct}%`}} />
        </div>
      </div>

      {/* Learn mode banner */}
      {sessionMode === "learn" && !finished && (
        <div
          className="border-b rule bg-[color:var(--paper)]"
          data-testid="learn-mode-banner"
        >
          <div className="max-w-4xl mx-auto px-6 py-3 flex items-center justify-between flex-wrap gap-3 text-sm">
            <div className="flex items-center gap-2 text-[color:var(--oxford)]">
              <Headphones size={14} weight="duotone"/>
              <span className="font-mono text-[10px] uppercase tracking-[0.25em]">Learn mode</span>
              <span className="text-[color:var(--ink-muted)] font-mono text-[10px]">· listen &amp; repeat · not scored</span>
              {voiceCommandsEnabled && (
                <span
                  className="hidden sm:inline-flex items-center gap-1 ml-2 pl-2 border-l rule text-[color:var(--ink-muted)] font-mono text-[10px]"
                  title="During the pause between reads, you can say any of these commands. Turn off in Profile."
                  data-testid="learn-voice-hint"
                >
                  <span aria-hidden="true">🎙</span>
                  <span className="hidden md:inline">Or say:</span>
                  <span>next · back · repeat · pause · stop</span>
                </span>
              )}
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <label className="flex items-center gap-2 text-xs">
                <span className="eyebrow">Pause</span>
                <select
                  value={learnPause}
                  onChange={(e) => setLearnPause(e.target.value)}
                  className="bg-white border rule px-2 py-1 text-xs font-mono uppercase"
                  data-testid="learn-pause-select"
                >
                  <option value="short">Short · 2s</option>
                  <option value="medium">Medium · 4s</option>
                  <option value="long">Long · 6s</option>
                  <option value="auto">Auto · scales with length</option>
                </select>
              </label>
              {!isFlashcardDeck && (
                <label className="flex items-center gap-2 text-xs cursor-pointer" title="Read each chunk twice (second time a bit slower)">
                  <input
                    type="checkbox"
                    checked={learnScriptRepeat}
                    onChange={(e) => {
                      setLearnScriptRepeat(e.target.checked);
                      cancelLearn();
                      // restart current card with new setting
                      setTimeout(() => { if (!pausedRef.current) runLearnCard(); }, 200);
                    }}
                    data-testid="learn-repeat-toggle"
                  />
                  <span>Re-read each chunk</span>
                </label>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Mode explainer popup */}
      {showModeHelp && (
        <div
          className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-start sm:items-center justify-center fade-in overflow-y-auto"
          onClick={(e) => { if (e.target === e.currentTarget) setShowModeHelp(false); }}
          data-testid="mode-help-modal"
          role="dialog"
          aria-modal="true"
          style={{
            paddingTop: "max(env(safe-area-inset-top, 0px), 16px)",
            paddingBottom: "max(env(safe-area-inset-bottom, 0px), 16px)",
            paddingLeft: "16px",
            paddingRight: "16px",
          }}
        >
          <div className="bg-white border rule w-full max-w-md my-auto">
            <div className="flex items-center justify-between border-b rule px-6 py-4">
              <div>
                <div className="eyebrow">Study modes</div>
                <h2 className="font-serif text-2xl leading-none mt-1">Which one should I use?</h2>
              </div>
              <button onClick={() => setShowModeHelp(false)} aria-label="Close" className="text-[color:var(--ink-muted)] hover:text-[color:var(--ink)]">
                <X size={20}/>
              </button>
            </div>
            <div className="px-6 py-5 space-y-4 text-sm leading-relaxed">
              <div>
                <div className="font-serif text-lg text-[color:var(--ink)]">🎯 Practice</div>
                <p className="text-[color:var(--ink-muted)] mt-1">
                  You answer each card out loud (or by typing). Ekkol scores you and tells you which cards you missed. Use this when you <strong>already know the material</strong> and want to test recall. Turn on <strong>Hands-free</strong> if you&apos;re walking or riding as a passenger — Ekkol reads the card, listens, grades, and auto-advances.
                </p>
              </div>
              <div>
                <div className="font-serif text-lg text-[color:var(--ink)]">🎧 Learn</div>
                <p className="text-[color:var(--ink-muted)] mt-1">
                  Ekkol reads everything to you — question, pause, answer, pause, next. No scoring, no mic needed, no pressure. Use this the <strong>first few times</strong> you go through a deck, when the material is still new, or whenever you just want to absorb the content without being tested. Great for the car, bus, or while cooking.
                </p>
              </div>
              <div className="border rule bg-[color:var(--surface-alt)] p-3 text-xs">
                <strong>Tip:</strong> A typical study routine is <strong>Learn → Practice → Hands-free Practice</strong>. Listen a few times to absorb, then switch to Practice to test yourself, then go Hands-free to lock it in while you&apos;re doing other things.
              </div>
            </div>
            <div className="flex items-center justify-between gap-3 border-t rule px-6 py-4 flex-wrap">
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => { setShowModeHelp(false); openPracticePrep(); }}
                  className="text-xs link-underline text-[color:var(--ink-muted)]"
                  data-testid="btn-reopen-practice-guide"
                >
                  Show the Practice guide
                </button>
                <span className="text-[color:var(--ink-faint)]">·</span>
                <button
                  onClick={() => { setShowModeHelp(false); openLearnPrep(); }}
                  className="text-xs link-underline text-[color:var(--ink-muted)]"
                  data-testid="btn-reopen-learn-guide"
                >
                  Show the Learn guide
                </button>
              </div>
              <button onClick={() => setShowModeHelp(false)} className="btn-primary" data-testid="btn-mode-help-close">
                Got it
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Settings drawer */}
      {showSettings && (
        <div className="border-b rule bg-white fade-in">
          <div className="max-w-4xl mx-auto px-6 py-5 grid md:grid-cols-4 gap-6">
            <div>
              <div className="eyebrow mb-2">Voice</div>
              <select
                value={voiceURI}
                onChange={(e) => setVoiceURI(e.target.value)}
                className="w-full bg-transparent border rule px-2 py-2 text-sm"
                data-testid="voice-select"
              >
                {voices.filter((v) => /^en/i.test(v.lang)).map((v) => (
                  <option key={v.voiceURI} value={v.voiceURI}>
                    {v.name}{v.localService ? "" : " · online"}
                  </option>
                ))}
              </select>
              <button
                onClick={() => speak("Hello — this is how I sound.", { rate, voiceURI })}
                className="text-xs link-underline mt-2"
                data-testid="btn-voice-preview"
              >Preview voice</button>
            </div>
            <div>
              <div className="eyebrow mb-2">Speed</div>
              <select value={rate} onChange={(e) => setRate(Number(e.target.value))} className="w-full bg-transparent border rule px-2 py-2 text-sm" data-testid="speed-select">
                <option value={0.75}>0.75×</option>
                <option value={1}>1×</option>
                <option value={1.25}>1.25×</option>
                <option value={1.5}>1.5×</option>
              </select>
            </div>
            <div>
              <div className="eyebrow mb-2">Match mode</div>
              <div className="grid grid-cols-2 gap-px bg-[color:var(--rule)] border rule">
                <button
                  onClick={() => setMatchMode("exact")}
                  className={`relative text-xs py-2 ${matchMode === "exact" ? "bg-[color:var(--ink)] text-white font-semibold" : "bg-white opacity-70"}`}
                  data-testid="session-mode-exact"
                >
                  {matchMode === "exact" && <CheckCircle size={12} weight="fill" className="inline mr-1"/>}
                  <Target size={12} className="inline mr-1"/>Exact
                </button>
                <button
                  onClick={() => setMatchMode("concept")}
                  className={`relative text-xs py-2 ${matchMode === "concept" ? "bg-[color:var(--ink)] text-white font-semibold" : "bg-white opacity-70"}`}
                  data-testid="session-mode-concept"
                >
                  {matchMode === "concept" && <CheckCircle size={12} weight="fill" className="inline mr-1"/>}
                  <Lightbulb size={12} className="inline mr-1"/>Concept
                </button>
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <div className="eyebrow">Threshold</div>
                <span className="font-mono text-xs">{threshold}%</span>
              </div>
              <input type="range" min="40" max="100" step="5" value={threshold}
                onChange={(e) => setThreshold(Number(e.target.value))}
                className="w-full accent-[color:var(--ink)] mt-2"
                data-testid="session-threshold-slider"/>
              <label className="flex items-center gap-2 mt-3 text-xs cursor-pointer">
                <input type="checkbox" checked={autoAdvance} onChange={(e) => setAutoAdvance(e.target.checked)} className="accent-[color:var(--ink)]" data-testid="session-auto-advance"/>
                Auto-advance on match
              </label>
            </div>
          </div>
          {voiceCommandsEnabled && (
            <div className="max-w-4xl mx-auto px-6 pb-4 text-[11px] text-[color:var(--ink-muted)] font-mono" data-testid="practice-voice-hint">
              HANDS-FREE VOICE COMMANDS: say &quot;next&quot;, &quot;previous&quot;, &quot;repeat&quot;, &quot;hint&quot;, &quot;pause&quot;, or &quot;stop&quot;.
            </div>
          )}
        </div>
      )}

      {/* Hero content */}
      <main className="flex-1 flex flex-col">
        <div className="flex-1 flex items-center justify-center px-6 py-10">
          <div className="max-w-2xl w-full text-center fade-in" data-testid="current-chunk">
            <div className="eyebrow mb-6">
              {isFlashcard ? "Flashcard" : deck.mode === "qa" ? "Statement card" : "Chunk"} {idx+1} ·
              {sessionMode === "learn"
                ? <span className="ml-2">Learn · listen &amp; repeat</span>
                : <span className="ml-2">{matchMode === "concept" ? "Concept match" : "Exact match"}</span>}
              {retries > 0 && sessionMode === "practice" && <span className="ml-2 text-[color:var(--vermilion)]">Retry {retries}</span>}
            </div>
            {isFlashcard && card.prompt ? (
              <>
                <p className="font-serif text-3xl sm:text-4xl md:text-5xl leading-snug tracking-tight">
                  {card.prompt}
                </p>
                {(scoredPass === true || sessionMode === "learn" || manualReveal) && (
                  <p className="mt-6 text-sm text-[color:var(--ink-muted)] font-mono uppercase tracking-[0.2em]" data-testid="card-answer-reveal">
                    Answer: <span className="font-serif normal-case tracking-normal text-base text-[color:var(--ink)]">{card.content}</span>
                  </p>
                )}
              </>
            ) : (
              <p className="font-serif text-3xl sm:text-4xl md:text-5xl leading-snug tracking-tight">
                {card.content}
              </p>
            )}

            <div className="mt-10 min-h-[64px]" aria-live="polite">
              {sessionMode === "learn" ? (
                <div className="text-sm text-[color:var(--ink-faint)] font-mono flex items-center justify-center gap-3 flex-wrap">
                  <span>{isSpeaking ? "Reading…" : "Pause — say it in your head or out loud…"}</span>
                  {isSpeaking && (
                    <button
                      onClick={() => setShowSettings(true)}
                      className="text-[10px] uppercase tracking-widest link-underline text-[color:var(--oxford)]"
                      data-testid="btn-change-voice-inline"
                    >
                      Change voice
                    </button>
                  )}
                </div>
              ) : transcriptFinal || transcriptInterim ? (
                <div className="border rule bg-white p-5 text-left">
                  <div className="eyebrow mb-2">You said</div>
                  <p className="font-serif text-lg leading-relaxed">
                    <span>{transcriptFinal}</span>
                    {transcriptInterim && <span className="text-[color:var(--ink-faint)]"> {transcriptInterim}</span>}
                  </p>
                </div>
              ) : (
                <div className="text-sm text-[color:var(--ink-faint)] font-mono">
                  {isListening ? "Listening…" : isSpeaking ? "Reading…" : handsFree ? "Hands-free active" : "Press the mic and repeat aloud"}
                </div>
              )}
            </div>

            {sessionMode === "practice" && matchScore !== null && (
              <>
                <div className="mt-6 inline-flex items-center gap-3 px-5 py-3 border rule bg-white" data-testid="match-score">
                  {scoredPass ? (
                    <CheckCircle size={22} weight="fill" className="text-[color:var(--success)]"/>
                  ) : (
                    <XCircle size={22} weight="fill" className="text-[color:var(--vermilion)]"/>
                  )}
                  <span className="font-mono text-lg">{Math.round(matchScore*100)}% match</span>
                  <span className="text-xs text-[color:var(--ink-muted)]">
                    {scoredPass ? "Locked in." : handsFree ? "Retrying…" : ""}
                    {scoreReason && <span className="ml-2 italic">— {scoreReason}</span>}
                  </span>
                </div>

                {/* Non-hands-free retry prompt. After a miss, users were uncertain
                    what to do next — the on-mic button looks idle and nothing
                    on screen tells them to tap it. This panel is the single
                    obvious next action. It also surfaces attempts remaining
                    before Ekkol auto-reveals so users feel progress, not
                    open-ended guessing. */}
                {!scoredPass && !handsFree && !manualReveal && (() => {
                  const usedMisses = (missCountRef.current[card.card_id] || 0);
                  const remaining = attemptsBeforeReveal > 0
                    ? Math.max(0, attemptsBeforeReveal - usedMisses)
                    : null;
                  return (
                    <div className="mt-4 mx-auto max-w-md border rule bg-white p-4 fade-in" data-testid="retry-prompt">
                      <p className="font-serif text-base leading-relaxed">
                        Not quite — <button
                          onClick={() => (isListening ? stopListening() : startListening(true))}
                          className="link-underline text-[color:var(--oxford)] font-semibold"
                          data-testid="btn-retry-mic"
                        >tap the mic and try again</button>.
                      </p>
                      {remaining !== null && (
                        <p className="mt-1 text-[11px] text-[color:var(--ink-faint)] font-mono uppercase tracking-widest">
                          {remaining === 0
                            ? "Answer will be revealed on your next try"
                            : remaining === 1
                              ? "1 try left before the answer is revealed"
                              : `${remaining} tries left before the answer is revealed`}
                        </p>
                      )}
                    </div>
                  );
                })()}
              </>
            )}

            {currentHint && (
              <div className="mt-5 mx-auto max-w-xl border rule bg-[color:var(--surface-alt)] p-4 text-left fade-in" data-testid="hint-panel">
                <div className="flex items-center gap-2 eyebrow mb-2 text-[color:var(--oxford)]">
                  <Lightbulb size={14} weight="fill"/> Hint
                </div>
                <p className="font-serif text-base leading-relaxed">{currentHint}</p>
              </div>
            )}
          </div>
        </div>

        {/* Controls */}
        <div
          className="border-t rule bg-white"
          style={{
            // Respect the device's bottom safe area (Android gesture bar,
            // iOS home indicator). Keeps the big red mic button from sitting
            // on top of the OS navigation strip on phones without hardware
            // nav keys. Falls back to 0px on desktop — no visual impact on
            // ekkol.app in Chrome/Safari on laptops.
            paddingBottom: "env(safe-area-inset-bottom, 0px)",
          }}
        >
          <div className="max-w-4xl mx-auto px-6 py-6 flex flex-col items-center gap-4">

            {/* Primary row — Prev · Mic/Play · Next. Always centered, always the hero action. */}
            <div className="w-full flex items-center justify-center gap-4 sm:gap-8">
              <button onClick={prev} disabled={idx===0} className="btn-ghost py-3 px-4 disabled:opacity-40" data-testid="btn-prev">
                <ArrowLeft size={16}/> <span className="hidden sm:inline">Prev</span>
              </button>

              <div className="flex flex-col items-center gap-2">
                {paused ? (
                  <button onClick={() => { setPaused(false); if (sessionMode === "learn") runLearnCard(); else readCurrent(); }} className="btn-primary" data-testid="btn-resume">
                    <Play size={14} weight="fill"/> Resume
                  </button>
                ) : sessionMode === "learn" ? (
                  <button
                    onClick={() => {
                      if (isSpeaking) { cancelLearn(); setPaused(true); }
                      else { setPaused(false); runLearnCard(); }
                    }}
                    className={`record-btn ${isSpeaking ? "active" : ""}`}
                    aria-label={isSpeaking ? "Pause listening" : "Start listening"}
                    data-testid="btn-learn-playpause"
                  >
                    {isSpeaking ? <Pause size={30} weight="fill"/> : <Play size={30} weight="fill"/>}
                  </button>
                ) : (
                  <button
                    onClick={() => (isListening ? stopListening() : startListening(true))}
                    className={`record-btn ${isListening ? "active" : ""}`}
                    aria-label={isListening ? "Stop recording" : "Start recording"}
                    data-testid="btn-record"
                  >
                    <Microphone size={30} weight="fill"/>
                  </button>
                )}
                <div className={`wave ${(isListening || (sessionMode === "learn" && isSpeaking)) ? "" : "idle"}`}>
                  <span/><span/><span/><span/><span/><span/><span/><span/>
                </div>
              </div>

              <button onClick={advance} className="btn-primary py-3 px-5" data-testid="btn-next">
                <span className="hidden sm:inline">{idx + 1 >= cards.length ? "Finish" : "Next"}</span>
                <span className="sm:hidden">{idx + 1 >= cards.length ? "Done" : "Next"}</span>
                <ArrowRight size={16} weight="bold"/>
              </button>
            </div>

            {/* Secondary row — compact toolbar of supporting actions. Icon-first; labels appear on ≥ sm. */}
            <div className="flex items-center justify-center flex-wrap gap-1.5 pt-1" data-testid="secondary-controls">
              <button onClick={repeat} className="btn-ghost py-2 px-3 text-xs" data-testid="btn-repeat" title="Re-read this card">
                <ArrowClockwise size={14}/> <span className="hidden sm:inline">Repeat</span>
              </button>
              {sessionMode === "practice" && (
                <button
                  onClick={() => requestHint()}
                  disabled={hintLoading}
                  className="btn-ghost py-2 px-3 text-xs text-[color:var(--oxford)]"
                  data-testid="btn-hint"
                  title="Get a hint (or say 'hint')"
                >
                  <Lightbulb size={14} weight={hintLoading ? undefined : "duotone"}/>
                  <span className="hidden sm:inline">{hintLoading ? "Thinking…" : "Hint"}</span>
                </button>
              )}
              {sessionMode === "practice" && isFlashcard && (
                <button
                  onClick={() => {
                    setManualReveal(true);
                    setRevealedCardIds((s) => { const n = new Set(s); n.add(card.card_id); return n; });
                    // SRS: manual peek — treat same as auto-reveal.
                    api.post(`/reviews/submit`, { deck_id: deckId, card_id: card.card_id, outcome: "peek" }).catch(() => {});
                    setFailedCards((prev) => prev.find((x) => x.card_id === card.card_id) ? prev : [...prev, card]);
                    setSessionResults((prev) => {
                      const exists = prev.find((r) => r.card_id === card.card_id);
                      if (exists) {
                        return prev.map((r) => r.card_id === card.card_id ? { ...r, revealed: true } : r);
                      }
                      return [...prev, {
                        card_id: card.card_id,
                        content: card.content,
                        prompt: card.prompt || null,
                        passed: false, score: 0, spoken: "",
                        revealed: true,
                      }];
                    });
                    const target = textToMatch(card);
                    if (target) {
                      try { recRef.current?.stop(); } catch {}
                      setIsListening(false);
                      cancelSpeech();
                      speak(`The answer is: ${target}.`, { rate, voiceURI: voiceURI || undefined });
                    }
                  }}
                  disabled={manualReveal || scoredPass !== null}
                  className="btn-ghost py-2 px-3 text-xs disabled:opacity-40"
                  data-testid="btn-show-answer"
                  title="Reveal the answer — card goes into your Retry pile"
                >
                  <Eye size={14}/> <span className="hidden sm:inline">{manualReveal || scoredPass !== null ? "Answer shown" : "Show answer"}</span>
                </button>
              )}
              {sessionMode === "practice" && (
                <button
                  onClick={() => (isSpeaking ? (cancelSpeech(), setIsSpeaking(false)) : readCurrent())}
                  className="btn-ghost py-2 px-3 text-xs"
                  data-testid="btn-listen"
                  title={isSpeaking ? "Stop playback" : "Replay the question"}
                >
                  {isSpeaking ? <Pause size={14} weight="fill"/> : <SpeakerHigh size={14}/>}
                  <span className="hidden sm:inline">{isSpeaking ? "Stop" : "Replay"}</span>
                </button>
              )}
              <button
                onClick={() => { setIdx(cards.length - 1); resetCardState(); }}
                className="btn-ghost py-2 px-3 text-xs hidden sm:inline-flex"
                aria-label="Skip to end"
                data-testid="btn-skip-end"
                title="Skip to the last card"
              >
                <FastForward size={14}/>
              </button>
            </div>
          </div>
          <div className="max-w-4xl mx-auto px-6 pb-4 text-center text-xs text-[color:var(--ink-muted)] font-mono">
            {sessionMode === "learn"
              ? (voiceCommandsEnabled
                  ? `LEARN MODE · not scored · tap Next/Pause — or say them during the pause`
                  : `LEARN MODE · not scored · use Next to skip, Pause to stop, or just listen and repeat`)
              : handsFree
                ? (voiceCommandsEnabled
                    ? "HANDS-FREE · Say \"next\", \"previous\", \"repeat\", \"hint\", \"pause\", or \"stop\" anytime"
                    : "HANDS-FREE · Voice commands are off — use the on-screen buttons to control the session")
                : `Speak after the reading stops · Pass at ${threshold}% or higher${attemptsBeforeReveal > 0 ? ` · Answer revealed after ${attemptsBeforeReveal} ${attemptsBeforeReveal === 1 ? "miss" : "misses"}` : ""}`}
          </div>
        </div>
      </main>
    </div>
  );
}
